<script>
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PhoneInput from 'dashboard/components/widgets/forms/PhoneInput.vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email } from '@vuelidate/validators';
import { maskCpf, maskCep } from 'dashboard/helper/inputMasks';

export default {
  components: {
    NextButton,
    PhoneInput,
  },
  props: {
    name: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    displayName: {
      type: String,
      default: '',
    },
    emailEnabled: {
      type: Boolean,
      default: false,
    },
    // Extended profile fields (custom_attributes)
    phoneNumber: {
      type: String,
      default: '',
    },
    address: {
      type: String,
      default: '',
    },
    addressComplement: {
      type: String,
      default: '',
    },
    city: {
      type: String,
      default: '',
    },
    state: {
      type: String,
      default: '',
    },
    zipCode: {
      type: String,
      default: '',
    },
    rg: {
      type: String,
      default: '',
    },
    cpf: {
      type: String,
      default: '',
    },
    managerName: {
      type: String,
      default: '',
    },
    restricted: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['updateUser'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      userName: this.name,
      userDisplayName: this.displayName,
      userEmail: this.email,
      userPhoneNumber: this.phoneNumber,
      phoneDialCode: '',
      userAddress: this.address,
      userAddressComplement: this.addressComplement,
      userCity: this.city,
      userState: this.state,
      userZipCode: this.zipCode,
      userRg: this.rg,
      userCpf: this.cpf,
      userManagerName: this.managerName,
      inputStyles: {
        borderRadius: '0.75rem',
        padding: '0.375rem 0.75rem',
        fontSize: '0.875rem',
        marginBottom: '0.125rem',
      },
    };
  },
  validations: {
    userName: {
      required,
      minLength: minLength(1),
    },
    userDisplayName: {},
    userEmail: {
      required,
      email,
    },
  },
  watch: {
    name: {
      handler(value) {
        this.userName = value;
      },
      immediate: true,
    },
    displayName: {
      handler(value) {
        this.userDisplayName = value;
      },
      immediate: true,
    },
    email: {
      handler(value) {
        this.userEmail = value;
      },
      immediate: true,
    },
    phoneNumber(value) {
      this.userPhoneNumber = value;
    },
    address(value) {
      this.userAddress = value;
    },
    addressComplement(value) {
      this.userAddressComplement = value;
    },
    city(value) {
      this.userCity = value;
    },
    state(value) {
      this.userState = value;
    },
    zipCode(value) {
      this.userZipCode = value;
    },
    rg(value) {
      this.userRg = value;
    },
    cpf(value) {
      this.userCpf = value;
    },
    managerName(value) {
      this.userManagerName = value;
    },
  },
  methods: {
    applyCpfMask() {
      this.userCpf = maskCpf(this.userCpf);
    },
    applyCepMask() {
      this.userZipCode = maskCep(this.userZipCode);
    },
    onPhoneInput(value) {
      this.userPhoneNumber = value;
    },
    onPhoneSetCode(code) {
      this.phoneDialCode = code;
    },
    async updateUser() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ERROR'));
        return;
      }
      // Build full phone number with dial code.
      // IMPORTANT: PhoneInput does NOT emit 'setCode' on initial mount/parse,
      // only on user interaction (keystroke or country selection).
      // If phoneDialCode is empty, the userPhoneNumber already contains
      // the full E.164 value (e.g. '+5511988552211') loaded from the DB.
      const fullPhone = this.phoneDialCode
        ? `${this.phoneDialCode}${this.userPhoneNumber}`
        : this.userPhoneNumber || '';
      this.$emit('updateUser', {
        name: this.userName,
        displayName: this.userDisplayName,
        email: this.userEmail,
        phoneNumber: fullPhone,
        address: this.userAddress,
        addressComplement: this.userAddressComplement,
        city: this.userCity,
        state: this.userState,
        zipCode: this.userZipCode,
        rg: this.userRg,
        cpf: this.userCpf,
        managerName: this.userManagerName,
      });
    },
  },
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="updateUser('profile')">
    <woot-input
      v-model="userName"
      :styles="inputStyles"
      :class="{ error: v$.userName.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.NAME.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.NAME.PLACEHOLDER')"
      :error="`${
        v$.userName.$error ? $t('PROFILE_SETTINGS.FORM.NAME.ERROR') : ''
      }`"
      @input="v$.userName.$touch"
      @blur="v$.userName.$touch"
    />
    <woot-input
      v-model="userDisplayName"
      :styles="inputStyles"
      :class="{ error: v$.userDisplayName.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.PLACEHOLDER')"
      :error="`${
        v$.userDisplayName.$error
          ? $t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.ERROR')
          : ''
      }`"
      @input="v$.userDisplayName.$touch"
      @blur="v$.userDisplayName.$touch"
    />
    <woot-input
      v-if="emailEnabled"
      v-model="userEmail"
      :styles="inputStyles"
      :class="{ error: v$.userEmail.$error }"
      :label="$t('PROFILE_SETTINGS.FORM.EMAIL.LABEL')"
      :placeholder="$t('PROFILE_SETTINGS.FORM.EMAIL.PLACEHOLDER')"
      :readonly="restricted"
      :error="`${
        v$.userEmail.$error ? $t('PROFILE_SETTINGS.FORM.EMAIL.ERROR') : ''
      }`"
      @input="v$.userEmail.$touch"
      @blur="v$.userEmail.$touch"
    />

    <!-- Extended Profile Fields -->
    <div class="border-t border-n-weak pt-4 mt-2">
      <h3 class="text-sm font-medium text-n-slate-11 mb-3">
        {{ $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.TITLE') }}
      </h3>
      <p
        v-if="restricted"
        class="text-xs text-n-slate-10 mb-3 italic"
      >
        {{ $t('PROFILE_SETTINGS.FORM.RESTRICTED_NOTICE') }}
      </p>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.PHONE.LABEL') }}
          </label>
          <PhoneInput
            :model-value="userPhoneNumber"
            :placeholder="
              $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.PHONE.PLACEHOLDER')
            "
            :styles="inputStyles"
            :disabled="restricted"
            @update:model-value="onPhoneInput"
            @set-code="onPhoneSetCode"
          />
        </div>
        <woot-input
          v-model="userCpf"
          :styles="inputStyles"
          :label="$t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.CPF.LABEL')"
          :placeholder="
            $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.CPF.PLACEHOLDER')
          "
          :readonly="restricted"
          @input="applyCpfMask"
        />
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <woot-input
          v-model="userRg"
          :styles="inputStyles"
          :label="$t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.RG.LABEL')"
          :placeholder="
            $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.RG.PLACEHOLDER')
          "
          :readonly="restricted"
        />
        <woot-input
          v-model="userManagerName"
          :styles="inputStyles"
          :label="$t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.MANAGER.LABEL')"
          :placeholder="
            $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.MANAGER.PLACEHOLDER')
          "
          :readonly="restricted"
        />
      </div>

      <woot-input
        v-model="userAddress"
        :styles="inputStyles"
        class="mt-4"
        :label="$t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.ADDRESS.LABEL')"
        :placeholder="
          $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.ADDRESS.PLACEHOLDER')
        "
        :readonly="restricted"
      />

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <woot-input
          v-model="userAddressComplement"
          :styles="inputStyles"
          :label="$t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.COMPLEMENT.LABEL')"
          :placeholder="
            $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.COMPLEMENT.PLACEHOLDER')
          "
          :readonly="restricted"
        />
        <woot-input
          v-model="userCity"
          :styles="inputStyles"
          :label="$t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.CITY.LABEL')"
          :placeholder="
            $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.CITY.PLACEHOLDER')
          "
          :readonly="restricted"
        />
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <woot-input
          v-model="userState"
          :styles="inputStyles"
          :label="$t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.STATE.LABEL')"
          :placeholder="
            $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.STATE.PLACEHOLDER')
          "
          :readonly="restricted"
        />
        <woot-input
          v-model="userZipCode"
          :styles="inputStyles"
          :label="$t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.ZIP_CODE.LABEL')"
          :placeholder="
            $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.ZIP_CODE.PLACEHOLDER')
          "
          :readonly="restricted"
          @input="applyCepMask"
        />
      </div>
    </div>

    <div>
      <NextButton type="submit" :label="$t('PROFILE_SETTINGS.BTN_TEXT')" />
    </div>
  </form>
</template>
